import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { updateProfilePicture, removeProfilePicture, formatApiError } from '../services/api';
import ProfilePicture from './ProfilePicture';

/**
 * ProfilePictureUpload Component
 * Handles profile picture upload, preview, and removal
 * @param {string} currentPictureUrl - Current profile picture URL
 * @param {string} initials - User initials for fallback display
 * @param {function} onUpdate - Callback when picture is updated (receives new user data)
 * @param {string} layout - 'default' | 'inline' - inline shows avatar + label + Choose File in a vertical stack (for profile example layout)
 */
const ProfilePictureUpload = ({ currentPictureUrl, initials, onUpdate, layout = 'default' }) => {
  const { t } = useTranslation(['profile', 'common']);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('invalidImageFormat'));
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(t('fileTooLarge'));
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    setError(null);
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedData = await updateProfilePicture(selectedFile);
      setSuccess(true);
      setPreviewUrl(null);
      setSelectedFile(null);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call onUpdate callback with new data
      if (onUpdate) {
        onUpdate(updatedData);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      const formattedError = formatApiError(err);
      setError(formattedError.generalError || formattedError.profile_picture?.[0] || t('uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!currentPictureUrl) return;

    if (!window.confirm(t('removeConfirm'))) {
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedData = await removeProfilePicture();
      setSuccess(true);

      // Call onUpdate callback with new data
      if (onUpdate) {
        onUpdate(updatedData);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      const formattedError = formatApiError(err);
      setError(formattedError.generalError || t('removeFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayUrl = previewUrl || currentPictureUrl;

  if (layout === 'inline') {
    return (
      <div className="flex flex-col items-center justify-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileSelect}
          className="hidden"
          id="profile-picture-input-inline"
          disabled={uploading}
        />
        <ProfilePicture
          profilePictureUrl={displayUrl}
          initials={initials}
          size="xl"
          className="mb-4"
        />
        <h3 className="font-semibold text-slate-900 mb-1 text-center">{t('pictureTitle')}</h3>
        <p className="text-xs text-slate-500 text-center mb-4">{t('pictureHintShort')}</p>
        <label
          htmlFor="profile-picture-input-inline"
          className={`w-full px-4 py-2 rounded-md text-sm font-medium border border-slate-300 bg-transparent hover:bg-slate-50 transition-colors cursor-pointer inline-flex items-center justify-center gap-2 ${
            uploading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <span className="material-symbols-outlined text-[18px]">upload</span>
          {selectedFile ? t('changeFile') : t('chooseFile')}
        </label>
        {selectedFile && (
          <div className="mt-3 flex items-center gap-2 w-full">
            <button
              onClick={handleUpload}
              disabled={uploading}
              type="button"
              className="flex-1 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-[18px]">upload</span>
              {t('common:upload')}
            </button>
            <button
              onClick={handleCancel}
              disabled={uploading}
              type="button"
              className="px-4 py-2 rounded-md text-sm font-medium border border-slate-300 bg-transparent hover:bg-slate-50"
            >
              {t('common:cancel')}
            </button>
          </div>
        )}
        {currentPictureUrl && !selectedFile && (
          <button
            onClick={handleRemove}
            disabled={uploading}
            type="button"
            className="mt-3 w-full px-4 py-2 rounded-md text-sm font-medium text-red-700 border border-red-200 hover:bg-red-50"
          >
            {t('common:remove')}
          </button>
        )}
        {success && (
          <p className="text-xs text-emerald-600 mt-2 text-center">{t('savedShort')}</p>
        )}
        {error && (
          <p className="text-xs text-red-600 mt-2 text-center">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Current/Preview Picture */}
      <div className="flex items-center gap-6">
        <ProfilePicture
          profilePictureUrl={displayUrl}
          initials={initials}
          size="xl"
          className="flex-shrink-0"
        />
        <div className="flex flex-col gap-2 flex-1">
          <h3 className="text-slate-700 text-sm font-semibold">{t('pictureTitle')}</h3>
          <p className="text-slate-500 text-xs">
            {t('pictureHintLong')}
          </p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 px-4 py-3 rounded-lg flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[20px]">check_circle</span>
          <span className="text-sm font-medium">{t('pictureUpdated')}</span>
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 px-4 py-3 rounded-lg flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[20px]">error</span>
          <span className="text-sm font-medium">{error}</span>
        </motion.div>
      )}

      {/* File Input and Actions */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileSelect}
            className="hidden"
            id="profile-picture-input"
            disabled={uploading}
          />
          <label
            htmlFor="profile-picture-input"
            className={`px-4 py-2 rounded-lg text-slate-700 text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer ${
              uploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {selectedFile ? t('changeFile') : t('chooseFile')}
          </label>
          {selectedFile && (
            <>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[18px]">upload</span>
                <span>{t('common:upload')}</span>
              </button>
              <button
                onClick={handleCancel}
                disabled={uploading}
                className="px-4 py-2 rounded-lg text-slate-700 text-sm font-semibold border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common:cancel')}
              </button>
            </>
          )}
          {currentPictureUrl && !selectedFile && (
            <button
              onClick={handleRemove}
              disabled={uploading}
              className="px-4 py-2 rounded-lg text-red-700 text-sm font-semibold border border-red-200 bg-white hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[18px]">delete</span>
              <span>{t('common:remove')}</span>
            </button>
          )}
        </div>
        {selectedFile && (
          <p className="text-slate-500 text-xs">
            {t('selectedFile', {
              name: selectedFile.name,
              size: (selectedFile.size / 1024 / 1024).toFixed(2)
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProfilePictureUpload;
